import cx from 'classnames';
import { useMemo } from 'react';
import { GENERIC_VEHICLE_IMAGE } from '~/constants/endpoints';
import {
  useGetIsWIPVehicle,
  useGetVehicleDetails
} from '~/context/vdp/VehicleContext';
import type { ImageMap } from '~/hooks/common/ui/useImageWithFallback';
import optimizeImage from '~/utilities/optimizeImage';

const genericHeroImageClasses = 'block h-full w-full rounded-md md:rounded-xl';

export enum ImageType {
  Generic = 'generic',
  HeliosHero = 'heliosHero',
  Hero = 'hero',
  JellyBean = 'jellyBean'
}

type ReturnProps = {
  heroImageMap: ImageMap<ImageType>;
  initialImageType: ImageType;
};

const imgWidth = 300;

export default function useGetCondensedHeroImages(): Partial<ReturnProps> {
  const isWIPVehicle = useGetIsWIPVehicle();
  const {
    stockImageData: { jellyBeanDesktopUrl = '' } = {},
    vexVdpImageData: {
      heroes: { hero = '', vdpHeliosHeroMobileURL = '' } = {}
    } = {}
  } = useGetVehicleDetails();

  // Image details used on render if the url is loaded
  const heroImageMap: ImageMap<ImageType> = useMemo(
    () => ({
      [ImageType.HeliosHero]: {
        classes: cx(genericHeroImageClasses, 'object-cover'),
        fallbackImageType: ImageType.Hero,
        imageType: ImageType.HeliosHero,
        shouldLogFallback: true,
        url: optimizeImage(vdpHeliosHeroMobileURL, { width: imgWidth })
      },
      [ImageType.Hero]: {
        classes: cx(genericHeroImageClasses, 'bg-background-weak object-cover'),
        fallbackImageType: ImageType.JellyBean,
        imageType: ImageType.Hero,
        shouldLogFallback: true,
        url: optimizeImage(hero, { width: imgWidth })
      },
      [ImageType.JellyBean]: {
        classes: cx(
          genericHeroImageClasses,
          'bg-background-weak object-cover md:object-contain'
        ),
        fallbackImageType: ImageType.Generic,
        imageType: ImageType.JellyBean,
        url: optimizeImage(jellyBeanDesktopUrl, { width: imgWidth })
      },
      [ImageType.Generic]: {
        classes: cx(
          genericHeroImageClasses,
          'bg-background-weak object-cover md:object-contain'
        ),
        imageType: ImageType.Generic,
        url: optimizeImage(GENERIC_VEHICLE_IMAGE, { width: imgWidth })
      }
    }),
    [hero, jellyBeanDesktopUrl, vdpHeliosHeroMobileURL]
  );

  return useMemo(
    () => ({
      heroImageMap,
      initialImageType: isWIPVehicle
        ? ImageType.JellyBean
        : ImageType.HeliosHero
    }),
    [heroImageMap, isWIPVehicle]
  );
}
